import React, { useEffect, useMemo, useState } from "react";

import { Container, Row, Col } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faCoin, faCartPlus, faTruck } from "@fortawesome/pro-light-svg-icons";
import { useNavigate, useParams } from "react-router";
import ImageViewer from "../../../components/ImageViewer";
import * as S from "./styles";
import { useTheme } from "styled-components";
import { Title, Label } from "../../main-styles";
import toast from "react-hot-toast";
import { getProduto, getProductVariants, notifyWhenAvailable } from "../../../api/produtos";
import { useDispatch, useSelector } from "react-redux";
import { addItem } from "../../../redux/slices/cartSlice";
import BadgeEsgotado from "../../../components/BadgeEsgotado";

function Produto({ readOnly = false }) {
  const { items } = useSelector((state) => state.cartReducer);
  const dispatch = useDispatch();
  const theme = useTheme();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [produto, setProduto] = useState(null);
  const [quantidade, setQuantidade] = useState(1);
  const [variants, setVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const thisItemInCart = items.find((item) => item.id === id);

  const estoque = useMemo(() => {
    if (!produto) return 0;
    let quantidadeNoCarrinho = thisItemInCart?.quantidade || 0;
    return produto.stock - quantidadeNoCarrinho || 0;
  }, [produto, thisItemInCart?.quantidade]);

  // quantidade minima de compra (nulo/0 e tratado como 1)
  const minQty = produto?.quantidade_minima || 1;

  const navigate = useNavigate();
  useEffect(() => {
    if (!id) return;
    _getProduto();
  }, [id]);

  async function _getProduto() {
    setLoading(true);
    try {
      let nproduto = await getProduto(id);
      console.log("nproduto", nproduto);
      setProduto(nproduto);
      // inicia a quantidade no minimo do produto
      setQuantidade(nproduto.quantidade_minima || 1);
      try {
        const vars = await getProductVariants(id);
        setVariants(vars || []);
        setSelectedVariant(null);
      } catch (e) {
        console.error("Error loading variants:", e);
      }
      setLoading(false);
    } catch (error) {
      toast.error("Erro ao carregar produto");
      navigate("/");
    }
  }

  useEffect(() => {
    preloadImages();
  }, [produto]);

  function preloadImages() {
    if (!produto) return;
    const images = [...produto.imagens];
    images.forEach((image) => {
      const img = new Image();
      img.src = image.replace(/\/500x500\//, "/350x350/");
    });
  }

  const displayPreco = selectedVariant ? selectedVariant.preco : produto?.preco;

  // aplicarMinimo: false enquanto o usuario digita (o minimo e validado no blur)
  function setQuantidadeClamped(value, { aplicarMinimo = true } = {}) {
    let n = parseInt(value, 10);
    if (isNaN(n)) {
      setQuantidade("");
      return;
    }
    if (aplicarMinimo && n < minQty) n = minQty;
    if (n > estoque) n = estoque;
    setQuantidade(n);
  }

  function handleClick() {
    if (variants.length > 0 && !selectedVariant) {
      toast.error("Selecione uma variante antes de adicionar ao carrinho");
      return;
    }
    const qtd = parseInt(quantidade, 10);
    if (isNaN(qtd) || qtd < 1) {
      toast.error("Informe uma quantidade válida");
      return;
    }
    if (qtd < minQty) {
      toast.error(`Quantidade mínima para este produto é ${minQty} unidades.`);
      return;
    }
    if (qtd > estoque) {
      toast.error(`Quantidade acima do estoque disponível (${estoque})`);
      return;
    }
    const itemToAdd = selectedVariant
      ? {
          ...produto,
          preco: selectedVariant.preco,
          id_variante: selectedVariant.id,
          variantLabel: selectedVariant.attributes
            ?.map((a) => a.valor)
            .join(" / "),
        }
      : produto;
    dispatch(
      addItem({
        item: itemToAdd,
        quantity: qtd,
      })
    );
    toast.success("Produto(s) adicionado ao carrinho!");
  }

  if (loading) {
    return (
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "40px 16px" }}>
        <Title>Carregando...</Title>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: 16 }}>
      {/* Breadcrumb / Voltar */}
      <div style={{ marginBottom: 16 }}>
        <Title onClick={() => navigate(-1)} style={{ cursor: "pointer" }}>
          <FontAwesomeIcon icon={faAngleLeft} /> Voltar
        </Title>
      </div>

      <S.ProductDetail>
        <S.ProductLayout>
          {/* Coluna Esquerda: Imagens */}
          <S.ImageSection>
            <ImageViewer
              images={[...produto?.imagens, ...produto?.videos] || []}
            />
          </S.ImageSection>

          {/* Coluna Direita: Info de Compra */}
          <S.BuySection>
            {produto?.vendidos > 100 && (
              <S.Condition>
                Novo | <a href="#">+{produto?.vendidos} vendidos</a>
              </S.Condition>
            )}
            <S.Nome>{produto?.nome}</S.Nome>

            {readOnly ? (
              <div>
                <S.StockInfo>
                  Estoque: <strong>{produto?.stock} unidades</strong>
                </S.StockInfo>
                <br />
                <div className="alert alert-warning">
                  Voce esta no modo gestor/admin,
                  <br />
                  nao e possivel adicionar ao carrinho.
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {produto?.stock < minQty || produto?.status === "pre_lancamento" ? (
                  <>
                    {produto?.status === "pre_lancamento" ? (
                      <BadgeEsgotado />
                    ) : (
                      <span
                        style={{
                          backgroundColor: "#9e9e9e",
                          color: "#fff",
                          padding: "4px 10px",
                          borderRadius: 4,
                          fontSize: 12,
                          fontWeight: 600,
                          width: "fit-content",
                        }}
                      >
                        ESGOTADO
                      </span>
                    )}
                    <S.NotifyButton
                      onClick={async () => {
                        try {
                          await notifyWhenAvailable(id);
                          toast.success(
                            "Você será notificado quando o produto estiver disponível."
                          );
                        } catch (e) {
                          toast.error("Erro ao cadastrar notificacao");
                        }
                      }}
                    >
                      Me avise quando disponivel
                    </S.NotifyButton>
                  </>
                ) : (
                  <>
                    <S.Preco>
                      <FontAwesomeIcon
                        icon={faCoin}
                        color={theme.primary}
                      />
                      {displayPreco}
                    </S.Preco>

                    {variants.length > 0 && (
                      <div>
                        <Label style={{ marginBottom: 8, display: "block" }}>Selecione uma opcao:</Label>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          {variants
                            .filter((v) => v.status === "active")
                            .map((v) => {
                              const label = v.attributes
                                ?.map((a) => a.valor)
                                .join(" / ");
                              return (
                                <S.VariantOption
                                  key={v.id}
                                  $selected={selectedVariant?.id === v.id}
                                  onClick={() => setSelectedVariant(v)}
                                >
                                  {label || v.id_everest}
                                </S.VariantOption>
                              );
                            })}
                        </div>
                      </div>
                    )}

                    <S.StockInfo>
                      Estoque disponivel: <strong>{estoque} unidades</strong>
                    </S.StockInfo>

                    {estoque >= minQty && (
                      <div>
                        <Label style={{ marginBottom: 6, display: "block" }}>
                          Quantidade:
                          <S.QtyHint>
                            (mín. {minQty} · máx. {estoque})
                          </S.QtyHint>
                        </Label>
                        <S.QtyWrapper>
                          <S.QtyButton
                            type="button"
                            disabled={parseInt(quantidade, 10) <= minQty}
                            onClick={() =>
                              setQuantidadeClamped((parseInt(quantidade, 10) || minQty) - 1)
                            }
                          >
                            −
                          </S.QtyButton>
                          <S.QtyInput
                            type="number"
                            min={minQty}
                            max={estoque}
                            value={quantidade}
                            onChange={(e) =>
                              setQuantidadeClamped(e.target.value, {
                                aplicarMinimo: false,
                              })
                            }
                            onBlur={() =>
                              setQuantidadeClamped(
                                quantidade === "" ? minQty : quantidade
                              )
                            }
                          />
                          <S.QtyButton
                            type="button"
                            disabled={parseInt(quantidade, 10) >= estoque}
                            onClick={() =>
                              setQuantidadeClamped((parseInt(quantidade, 10) || 0) + 1)
                            }
                          >
                            +
                          </S.QtyButton>
                        </S.QtyWrapper>
                      </div>
                    )}

                    {estoque <= 0 ? (
                      <S.Esgotado>
                        Voce adicionou o maximo de itens no carrinho
                      </S.Esgotado>
                    ) : estoque < minQty ? (
                      <S.Esgotado>
                        Estoque insuficiente para o pedido mínimo de {minQty}{" "}
                        unidades.
                      </S.Esgotado>
                    ) : (
                      <S.BuyButton onClick={handleClick}>
                        <FontAwesomeIcon icon={faCartPlus} />
                        Adicionar ao carrinho
                      </S.BuyButton>
                    )}
                  </>
                )}
              </div>
            )}
          </S.BuySection>
        </S.ProductLayout>

        {/* Descricao */}
        <S.DescriptionSection>
          <h2 style={{ fontSize: 24, fontWeight: 600, color: "#333", marginBottom: 20 }}>
            Descricao
          </h2>
          <div
            style={{ fontSize: 16, color: "#666", lineHeight: 1.6 }}
            dangerouslySetInnerHTML={{ __html: produto?.descricao }}
          />
        </S.DescriptionSection>
      </S.ProductDetail>

      <div style={{ height: 50 }} />
    </div>
  );
}

export default Produto;
